import React, { useState, useEffect, useRef } from "react";
import SmallScreenView from "../smallscreen";
import { Hidden } from "react-awesome-styled-grid";
import {
  Button,
  Spin,
  message,
} from 'antd';
import { Base64 } from "js-base64";
import { AmountDivideByHundred } from "../../helper";
import {
  FlexContainer,
  H1Text,
} from "../../design";
import PaymentError from "../paymenterror";
import {
  getPayPalClientToken,
} from '../../api';
import { SpText } from "../../widgetDesign";
import GooglePayIcon from "../../assets/imgs/GOOGLE_PAY.svg";

type Props = {
  headerText: string,
  payment: Object;
  chargeByGooglePay: Function;
  errorHeader: String,
  gpayError: String,
  accountGid: String,
  setTransactionModalOpen: Function,
  paypalGooglePayRecaptchaToken: String,
};

const paypal_sdk_url = "https://www.paypal.com/sdk/js";
const google_pay_sdk_url = "https://pay.google.com/gp/p/js/pay.js";
const currency = "USD";
const intent = "capture";

async function script_to_head(attrs: Record<string, string>) {
  return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      Object.entries(attrs).forEach(([k, v]) => script.setAttribute(k, v));
      script.onload = () => resolve();
      script.onerror = () => reject();
      document.head.appendChild(script);
  });
}

const PayPalGooglePay = (props: Props) => {
  const {
    headerText,
    payment,
    chargeByGooglePay,
    gpayError,
    errorHeader,
    accountGid,
    setTransactionModalOpen,
    paypalGooglePayRecaptchaToken = null,
  } = props;

  const [loading, setLoading] = useState(false);
  const [eligible, setEligible] = useState(false);
  const [googleSDKReady, setGoogleSDKReady] = useState(false);
  const [paypalSDKReady, setPaypalSDKReady] = useState(false);
  const [googlePayPaymentsClient, setGooglePayPaymentsClient] = useState(null);
  const paypalGooglePayConfig = useRef(null);

  // load both paypal sdk and google sdk
  const loadSDKs = async () => {
    try {
      const data = await getPayPalClientToken(accountGid);
      const value = Base64.decode(data);
      const value_json = JSON.parse(value);
      await script_to_head({
        id: 'paypalSDK',
        src: `${paypal_sdk_url}?client-id=${value_json?.clientId}&currency=${currency}&intent=${intent}&components=buttons,googlepay`,
        "data-client-token": value_json?.token?.client_token,
      });
      await script_to_head({
        id: 'googlePaySDK',
        src: google_pay_sdk_url,
        async: true,
      });
    } catch (err) {
      setEligible(false);
    }
  };

  useEffect(() => {
    const checkGooglePaySDKInterval = setInterval(() => {
      if (window.google?.payments?.api) {
        setGoogleSDKReady(true);
        clearInterval(checkGooglePaySDKInterval);
      }
    }, 50);
    const checkPaypalSDKInterval = setInterval(() => {
      if (window.paypal) {
        setPaypalSDKReady(true);
        clearInterval(checkPaypalSDKInterval);
      }
    }, 50);
    loadSDKs();
    setLoading(true);
    return () => {
      clearInterval(checkGooglePaySDKInterval);
      clearInterval(checkPaypalSDKInterval);
      document.getElementById('paypalSDK')?.remove();
      document.getElementById('googlePaySDK')?.remove();
    };
  }, []);

  const getGooglePaymentsClient = () => {
    const usageMode = process.env.REACT_APP_PAYPAL_GOOGLE_PAY_MODE ?? 'TEST';
    return new window.google.payments.api.PaymentsClient({environment: String(usageMode)});
  };

  const constructGooglePayPaymentDataRequest = () => {
    if (!paypalGooglePayConfig.current || !payment) return;
    const paymentDataRequest = {
      apiVersion: paypalGooglePayConfig.current?.apiVersion,
      apiVersionMinor: paypalGooglePayConfig.current?.apiVersionMinor,
      emailRequired: true,
      merchantInfo: {
        merchantName: 'Swirepay',
        merchantId: paypalGooglePayConfig.current?.merchantInfo?.merchantId,
      },
      allowedPaymentMethods: [{
        type: paypalGooglePayConfig.current?.allowedPaymentMethods?.[0]?.type,
        parameters: {
          allowedAuthMethods: paypalGooglePayConfig.current?.allowedPaymentMethods?.[0]?.parameters?.allowedAuthMethods,
          allowedCardNetworks: paypalGooglePayConfig.current?.allowedPaymentMethods?.[0]?.parameters?.allowedCardNetworks,
          billingAddressRequired: paypalGooglePayConfig.current?.allowedPaymentMethods?.[0]?.parameters?.billingAddressRequired,
          billingAddressParameters: {
            format: paypalGooglePayConfig.current?.allowedPaymentMethods?.[0]?.parameters?.billingAddressParameters?.format,
            phoneNumberRequired: true,
          },
        },
        tokenizationSpecification: {
          type: paypalGooglePayConfig.current?.allowedPaymentMethods?.[0]?.tokenizationSpecification?.type,
          parameters: {
            gateway: paypalGooglePayConfig.current?.allowedPaymentMethods?.[0]?.tokenizationSpecification?.parameters?.gateway,
            gatewayMerchantId: paypalGooglePayConfig.current?.allowedPaymentMethods?.[0]?.tokenizationSpecification?.parameters?.gatewayMerchantId,
          },
        },
      }],
      transactionInfo: {
        countryCode: payment?.currency?.countryAlpha2,
        currencyCode: payment?.currency?.name,
        totalPriceStatus: 'FINAL',
        totalPrice: AmountDivideByHundred(payment.amount),
        checkoutOption: 'COMPLETE_IMMEDIATE_PURCHASE',
      }
    };
    const allowedPaymentMethods = paypalGooglePayConfig.current?.allowedPaymentMethods;
    const baseRequest = {
      apiVersion: paypalGooglePayConfig.current?.apiVersion,
      apiVersionMinor: paypalGooglePayConfig.current?.apiVersionMinor,
    };
    const tokenizationSpecification = paypalGooglePayConfig.current?.allowedPaymentMethods?.[0]?.tokenizationSpecification;
    return {
      allowedPaymentMethods,
      paymentDataRequest,
      baseRequest,
      tokenizationSpecification,
    };
  };

  useEffect(() => {
    if (!googleSDKReady
      || !paypalSDKReady
    ) return;
    const paymentsClient = getGooglePaymentsClient();
    setGooglePayPaymentsClient(paymentsClient);
    window.paypal?.Googlepay?.()?.config()?.then((currentPaypalGooglePayConfig) => {
      paypalGooglePayConfig.current = currentPaypalGooglePayConfig;
      const { allowedPaymentMethods, baseRequest } = constructGooglePayPaymentDataRequest();
      setLoading(false);
      paymentsClient?.isReadyToPay({ ...baseRequest, allowedPaymentMethods, })
      ?.then((response) => {
        if (response.result && currentPaypalGooglePayConfig.isEligible) {
          setEligible(true);
          return;
        }
        setEligible(false);
      })?.catch((err) => {
        console.error('Not Eligible: ', err);
        setEligible(false);
      });
    })?.catch((err) => {
      console.error('Could not fetch PayPal Google Pay config: ', err);
      message.error('Could not Load Google Pay');
      setLoading(false);
      setEligible(false);
    });
  }, [googleSDKReady, paypalSDKReady]);

  const handleGooglePayButtonClick = async () => {
    if (!googlePayPaymentsClient) {
      console.error('no paymentsClient');
      message.error('GooglePay Error');
      return;
    }
    setTransactionModalOpen(true);
    const { paymentDataRequest } = constructGooglePayPaymentDataRequest();
    try {
      const paymentData = await googlePayPaymentsClient.loadPaymentData(paymentDataRequest);
      await chargeByGooglePay(paymentData);
    } catch (err) {
      console.error('exception around loadPaymentData:', err);
      setTransactionModalOpen(false);
      message.error('GooglePay Transaction Failed');
    }
  };

  // auto re-submit
  useEffect(() => {
    if (paypalGooglePayRecaptchaToken) {
      handleGooglePayButtonClick();
    }
  }, [paypalGooglePayRecaptchaToken]);


  if (loading) {
    return (
      <Spin
        spinning={loading}
        delay={300}
        size="large"
        tip="Please Wait..."
      />
    );
  }

  if (!eligible) {
    return (
      <SpText>Not Eligible!</SpText>
    );
  }

  return (
    <>
      <H1Text
        className="px-4 px-md-4 mb-0"
      >
        <SmallScreenView>
          {"Pay With Google pay"}
        </SmallScreenView>
        <Hidden sm xs>
          {headerText || "Pay With Google pay"}
        </Hidden>
      </H1Text>
      <FlexContainer
        flexDirection="column"
      >
        <div className="px-4 px-md-4 mt-4 mb-4 w-100">
          <div className="row m-0">
            <Button
              size="large"
              onClick={handleGooglePayButtonClick}
              style={{
                backgroundColor: '#000',
                color: '#fff',
                borderColor: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <img src={GooglePayIcon} alt="Google Icon" />
              <span>Pay with GooglePay</span>
            </Button>
          </div>
        </div>
        {/* <div className="px-1 px-md-2">
          { gpayError && (<PaymentError header={errorHeader} message={gpayError} />) }
        </div>*/}
      </FlexContainer>
    </>
  )
}

export default PayPalGooglePay;
